import { Status } from '@/Enums/Enums';
import type { Association } from '@/Typings';
import type { PropertyRow } from '@/Datastructures';
import type {
    CheckBoxViewModel as CheckBoxViewModelT,
    TextBoxViewModel,
    LabelViewModel,
} from 'radpresentation.htmlclient.defaultviewextensions';
import {
    Utilities,
    CheckBoxViewModelBuilder,
    LocatorHelper,
} from 'radpresentation.htmlclient.defaultviewextensions';
import MemberTabViewModel from '@/ViewModels/Controls/MemberTabViewModel';
import type EditMaskTabViewModel from '@/ViewModels/Controls/EditMaskTabViewModel';

class AssociationTabViewModel extends MemberTabViewModel {
    public MandatoryWhenLabel: LabelViewModel;
    public MandatoryWhen: TextBoxViewModel;
    public Mandatory: CheckBoxViewModelT;
    private Indexable: CheckBoxViewModelT;

    // injected
    public editMaskTabViewModel: EditMaskTabViewModel;

    public constructor(locator: KnockoutObservable<string>) {
        super(locator);

        this.createMandatory();
        this.createMandatoryWhen();
        this.createIndexable();

        this.setUpEnabledStatesConfiguration();
        this.syncFocusableToDisabled(this.editableStaticPropertyFields);
        this.syncFocusableToDisabled(this.Name);
    }

    public dispose(): void {
        super.dispose();
    }

    public updateContent(relatedData?: PropertyRow): void {
        this.isInUpdateMode = true;

        if (!relatedData) {
            relatedData = {} as PropertyRow;
        }
        this.relatedData = relatedData;

        super.updateContent();
        this.Name.value(relatedData.Name || '');
        this.Indexable.isChecked(relatedData.Indexable == true);
        this.Mandatory.isChecked(relatedData.Mandatory == true);
        this.MandatoryWhen.value(relatedData.MandatoryWhen || '');

        this.updateEnabledStates(relatedData);

        this.isInUpdateMode = false;
    }

    private updateEnabledStates(relatedData: PropertyRow): void {
        if (_.isEmpty(relatedData)) {
            return;
        }

        let isDynamic = relatedData.Dynamisch === true;
        let hasEmptyName = Utilities.isBlank(relatedData.Name);

        this.editableStaticPropertyFields.forEach(field =>
            field.isEnabled(true)
        );

        this.Name.isEnabled(isDynamic && hasEmptyName);
    }

    public disableAllControls() {
        this.editableStaticPropertyFields.forEach(field =>
            field.isEnabled(false)
        );
        this.Name.isEnabled(false);
    }

    private setUpEnabledStatesConfiguration(): void {
        this.editableStaticPropertyFields = [
            this.ErgonomicIdentifier,
            this.ErgonomicIdentifier.multilineTextBox,
            this.ErgonomicIdentifier.link,
            this.Tooltip,
            this.Tooltip.multilineTextBox,
            this.Tooltip.link,
            this.Help,
            this.Help.multilineTextBox,
            this.Help.link,
            this.Mandatory,
            this.MandatoryWhen,
            this.Indexable,
        ];
    }

    private createMandatory(): void {
        this.Mandatory = new CheckBoxViewModelBuilder()
            .withLabel(
                this.LabelViewModel.create('RADEM.RADEXTENSIONMANAGER.REQUIRED')
            )
            .withEnabledState(false)
            .withIsCheckedSubscription(
                this.mandatoryCheckedSubscription.bind(this)
            )
            .withLocator(this.locator() + '.required')
            .create();
    }

    private createMandatoryWhen(): void {
        this.MandatoryWhenLabel = this.LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.REQUIREDWHEN'
        );
        this.MandatoryWhen = new this.TextBoxViewModel({
            isEnabled: false,
            locator: LocatorHelper.buildLocator(this.locator, 'mandatoryWhen'),
        });

        const subscription = this.MandatoryWhen.value.subscribe(
            this.mandatoryWhenSubscription.bind(this)
        );
        this.subscriptions.push(subscription);
    }

    private createIndexable(): void {
        this.Indexable = new CheckBoxViewModelBuilder()
            .withLabel(
                this.LabelViewModel.create(
                    'RADEM.RADEXTENSIONMANAGER.INDEXABLE'
                )
            )
            .withEnabledState(false)
            .withIsCheckedSubscription(
                this.indexableCheckedSubscription.bind(this)
            )
            .withLocator(this.locator() + '.indexable')
            .create();
    }

    private mandatoryCheckedSubscription(isChecked: boolean): void {
        if (this.isInUpdateMode) {
            return;
        }

        this.updateAssociationInJSON('Mandatory', isChecked);
        this.relatedData.Mandatory = isChecked;

        this.updateVisibleInEditMode(isChecked, this.MandatoryWhen.value());
    }

    private mandatoryWhenSubscription(mandatoryWhen: string): void {
        if (this.isInUpdateMode) {
            return;
        }

        this.updateAssociationInJSON('MandatoryWhen', mandatoryWhen);
        this.relatedData.MandatoryWhen = mandatoryWhen;

        this.updateVisibleInEditMode(this.Mandatory.isChecked(), mandatoryWhen);
    }

    private updateVisibleInEditMode(mandatory: boolean, mandatoryWhen: string) {
        if (!mandatory && !Utilities.isNullOrUndefinedOrBlank(mandatoryWhen)) {
            this.editMaskTabViewModel.VisibleInEditMode.isChecked(true);
            this.editMaskTabViewModel.VisibleInEditMode.isEnabled(false);
        } else {
            // re-evaluate isEnabled-rule for visibleInEditMode
            this.editMaskTabViewModel.VisibleInEditMode.isEnabled(true);
        }
    }

    private indexableCheckedSubscription(isChecked: boolean): void {
        if (this.isInUpdateMode) {
            return;
        }

        this.updateAssociationInJSON('Indexable', isChecked);
        this.relatedData.Indexable = isChecked;
    }

    private updateAssociationInJSON(fieldName: keyof Association, value: any) {
        let association = this.getAssociation();

        association[fieldName as any] = value;

        Status.setChanged(association);
    }

    private getAssociation(): Association {
        let context = this.context;
        let memberPdoId = this.DVGItem.Member.PDO_ID;

        return context.getAssociationMetaByPdoId(memberPdoId);
    }
}

export default AssociationTabViewModel;
